/**
 * Draft handoff between the `client_create_agent` client tool and the
 * Agent editor.
 *
 * The tool stashes a JSON draft under the storage key below and
 * navigates the user to ``/agents/new``. The editor consumes the draft
 * once on mount and clears the slot, so refreshing the page or opening
 * ``/agents/new`` directly from another entry point produces the usual
 * empty form.
 *
 * The shape is wire-friendly (matches the JSON the LLM emits) rather
 * than identical to the editor's `FormState`. Mapping happens at the
 * editor's seam — see `buildRequestTemplate.ts`.
 *
 * SessionStorage is a trust boundary: values can be tampered with via
 * DevTools, browser extensions, or left behind by an older build with a
 * different schema. The consume path runs a runtime shape validator so
 * the editor's lazy state initializer never sees a malformed payload.
 */

const AGENT_DRAFT_STORAGE_KEY = "roboto.agent-draft";

/**
 * Label-keyed description map for a triage goal. Mirrors the wire shape
 * the server stores (`label_vocabulary: Record<label, description>`)
 * so the LLM can produce it directly without an array-of-objects detour.
 */
export interface AgentDraftDatasetTriageGoal {
  goal_type: "dataset_triage";
  label_vocabulary: Record<string, string>;
}

export interface AgentDraftDatasetSummaryGoal {
  goal_type: "dataset_summary";
  summary_format_spec_prompt?: string;
}

export type AgentDraftGoal =
  | AgentDraftDatasetSummaryGoal
  | AgentDraftDatasetTriageGoal;

export interface AgentDraftVariable {
  name: string;
  description?: string;
  default?: string;
}

/**
 * Partial Agent specification handed from the client tool to the editor.
 *
 * Every field is optional because the LLM should be allowed to emit
 * what it knows and let the user fill the rest in the editor. The
 * editor maps missing fields back to its own empty-form defaults.
 */
export interface AgentDraft {
  name?: string;
  description?: string;
  /** Model-profile id. Omit to use the org default. */
  model_profile?: string;
  /** Optional user message seeded into the agent's first turn. */
  kickoff_message?: string;
  goals?: AgentDraftGoal[];
  variables?: AgentDraftVariable[];
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asGoal(value: unknown): AgentDraftGoal | null {
  if (!isPlainObject(value)) {
    return null;
  }
  if (value.goal_type === "dataset_summary") {
    const prompt = asString(value.summary_format_spec_prompt);
    return prompt === undefined
      ? { goal_type: "dataset_summary" }
      : { goal_type: "dataset_summary", summary_format_spec_prompt: prompt };
  }
  if (value.goal_type === "dataset_triage") {
    // `label_vocabulary` is the field the editor's downstream consumer
    // (`draftToFormState`) iterates via `Object.entries`. A missing or
    // wrong-typed value here is the canonical "malformed stash crashes
    // the editor" failure mode — drop the goal entirely rather than
    // forward a half-formed shape.
    if (!isPlainObject(value.label_vocabulary)) {
      return null;
    }
    const vocab: Record<string, string> = {};
    for (const [label, description] of Object.entries(value.label_vocabulary)) {
      if (typeof description === "string") {
        vocab[label] = description;
      }
    }
    return { goal_type: "dataset_triage", label_vocabulary: vocab };
  }
  return null;
}

function asVariable(value: unknown): AgentDraftVariable | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const name = asString(value.name);
  if (name === undefined || name === "") {
    return null;
  }
  const entry: AgentDraftVariable = { name };
  const description = asString(value.description);
  if (description !== undefined) {
    entry.description = description;
  }
  const defaultValue = asString(value.default);
  if (defaultValue !== undefined) {
    entry.default = defaultValue;
  }
  return entry;
}

/**
 * Coerce arbitrary parsed JSON into an {@link AgentDraft}.
 *
 * Returns `null` if the value isn't a plain object. Otherwise returns
 * a draft that contains *only* fields whose runtime shape matches the
 * type — wrong-typed fields are dropped, wrong-typed goal / variable
 * entries are dropped from their arrays. This means a partially-corrupt
 * stored draft yields a partially-populated form rather than a crash,
 * and the editor's existing validation surfaces any remaining gaps.
 *
 * Exposed for tests; callers should use {@link consumeAgentDraft}.
 */
export function parseAgentDraftShape(value: unknown): AgentDraft | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const draft: AgentDraft = {};
  const name = asString(value.name);
  if (name !== undefined) {
    draft.name = name;
  }
  const description = asString(value.description);
  if (description !== undefined) {
    draft.description = description;
  }
  const modelProfile = asString(value.model_profile);
  if (modelProfile !== undefined) {
    draft.model_profile = modelProfile;
  }
  const kickoff = asString(value.kickoff_message);
  if (kickoff !== undefined) {
    draft.kickoff_message = kickoff;
  }
  if (Array.isArray(value.goals)) {
    const goals: AgentDraftGoal[] = [];
    for (const raw of value.goals) {
      const goal = asGoal(raw);
      if (goal !== null) {
        goals.push(goal);
      }
    }
    if (goals.length > 0) {
      draft.goals = goals;
    }
  }
  if (Array.isArray(value.variables)) {
    const variables: AgentDraftVariable[] = [];
    for (const raw of value.variables) {
      const variable = asVariable(raw);
      if (variable !== null) {
        variables.push(variable);
      }
    }
    if (variables.length > 0) {
      draft.variables = variables;
    }
  }
  return draft;
}

// ---------------------------------------------------------------------------
// Consume cache
//
// React 18 StrictMode (dev) mounts → unmounts → remounts every component
// on initial render to surface bugs in cleanup logic. The consume side
// effect ("read sessionStorage then remove the slot") is not idempotent
// across that cycle: the first mount reads the draft and clears the
// slot; the synthetic remount sees an empty slot and would return
// `null`, overwriting whatever state the first mount had committed.
//
// The cache below preserves the consume result across one short window
// (current task + queued macrotask) so the synthetic remount returns
// the same draft. A subsequent fresh visit to the editor (after a true
// navigation away and back) sees the cache cleared and reads from
// sessionStorage normally. `stashAgentDraft` also invalidates the cache
// so any new write supersedes an older cached read.
// ---------------------------------------------------------------------------

interface ConsumeCache {
  value: AgentDraft | null;
}

let consumeCache: ConsumeCache | undefined = undefined;
let consumeCacheClearScheduled = false;

function scheduleConsumeCacheClear(): void {
  if (consumeCacheClearScheduled) {
    return;
  }
  consumeCacheClearScheduled = true;
  // setTimeout(0) fires after the current task and any microtasks it
  // queues. React's StrictMode synthetic remount completes within that
  // same task, so the second mount sees the cache; visits that happen
  // later (after navigation) start a new task and find the cache
  // cleared. `requestAnimationFrame` would also work but isn't
  // available in every test runtime.
  setTimeout(() => {
    consumeCache = undefined;
    consumeCacheClearScheduled = false;
  }, 0);
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/**
 * Write the draft to sessionStorage. Silently no-ops if storage is
 * unavailable (e.g. private-mode quirks); the editor handles the
 * resulting empty draft as if the user had opened the page directly.
 */
export function stashAgentDraft(draft: AgentDraft): void {
  try {
    sessionStorage.setItem(AGENT_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage unavailable — fall back to the editor's empty form.
  }
  // Invalidate any cached read so the next `consumeAgentDraft` sees
  // this stash, not the prior visit's value.
  consumeCache = undefined;
}

/**
 * Read, validate, and clear the pending draft.
 *
 * Returns `null` when no draft is pending, when the stored payload
 * fails to parse, or when the parsed value isn't a plain object. Any
 * nested field whose runtime shape doesn't match its declared type is
 * dropped silently via {@link parseAgentDraftShape}; the editor
 * surfaces the gaps via its own validation rather than crashing.
 *
 * The slot is cleared even on parse failure so a corrupt entry can't
 * wedge subsequent editor visits. The result is briefly cached at the
 * module level — see the comment block above — so a React 18
 * StrictMode synthetic remount sees the same draft as the first mount
 * rather than an empty slot.
 */
export function consumeAgentDraft(): AgentDraft | null {
  if (consumeCache !== undefined) {
    return consumeCache.value;
  }
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(AGENT_DRAFT_STORAGE_KEY);
  } catch {
    return null;
  }
  let parsed: AgentDraft | null;
  if (raw === null) {
    parsed = null;
  } else {
    try {
      sessionStorage.removeItem(AGENT_DRAFT_STORAGE_KEY);
    } catch {
      // Best-effort clear.
    }
    try {
      parsed = parseAgentDraftShape(JSON.parse(raw));
    } catch {
      parsed = null;
    }
  }
  consumeCache = { value: parsed };
  scheduleConsumeCacheClear();
  return parsed;
}

/**
 * Internal hook for tests — exported so tests can reset state between
 * cases without poking at the storage key directly.
 */
export const __AGENT_DRAFT_STORAGE_KEY_FOR_TESTS: string =
  AGENT_DRAFT_STORAGE_KEY;

/**
 * Internal hook for tests — synchronously clear the module-level
 * consume cache so each test starts from a clean state. Tests
 * normally let the setTimeout-based clear handle this, but a tight
 * sequence of stash+consume cycles in one test needs an explicit
 * reset to avoid the previous test's cache bleeding through.
 */
export function __resetAgentDraftCacheForTests(): void {
  consumeCache = undefined;
  consumeCacheClearScheduled = false;
}
